import React, { useEffect, useRef } from "react";
import {
  FaLinkedin, FaGithub, FaTwitter, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaArrowUp, FaInstagram, FaFacebook, FaYoutube, FaRss, FaPaperPlane,
  FaShieldAlt, FaCreditCard, FaGoogle, FaApple, FaAndroid
} from "react-icons/fa";
import { FiChevronUp, FiCheck } from "react-icons/fi";
import { Link } from "react-router-dom";

const Footer = () => {
  const footerRef = useRef(null);
  const year = new Date().getFullYear();

  const businessInfo = {
    name: "Codriva Tech",
    email: "codrivatech.contact@gmail.com",
    phone: "+91 9301542085",
    address: "Hathi Khana Rd, near pushp vatika, Garam Sadak, Nagar Nigam Colony, Morar, Gwalior, Madhya Pradesh 474006",
    hours: "Mon-Fri: 9:00 AM - 6:00 PM",
    founder: "Sudhanshu Singh",
    established: "2020",
    industry: "Information Technology & Services"
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'Home', path: '/', description: 'Main landing page' },
    { label: 'Services', path: '/services', description: 'Our IT services' },
    { label: 'Portfolio', path: '/portfolio', description: 'Project showcase' },
    { label: 'About Us', path: '/about', description: 'Company information' },
    { label: 'Contact', path: '/contact', description: 'Contact information' },
  ];

  const expertiseList = [
    'Web Development',
    'Mobile App Development',
    'Custom Software Solutions',
    'Cloud & DevOps Services',
    'UI/UX Design',
    'Digital Marketing',
    'SEO Services',
    'E-commerce Solutions',
    'AI & Machine Learning',
    'IT Consulting',
    'Maintenance & Support'
  ];

  return (
    <footer className="footer" ref={footerRef} itemScope itemType="https://schema.org/Organization">
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": businessInfo.name,
            "url": "https://codrivatech.com",
            "logo": "https://codrivatech.com/companylogo.png",
            "description": "Premier IT development company specializing in web, mobile app, and software development solutions.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": businessInfo.address.split(',')[0],
              "addressLocality": "Gwalior",
              "addressRegion": "Madhya Pradesh",
              "postalCode": "474006",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": businessInfo.phone,
              "contactType": "customer service",
              "email": businessInfo.email,
              "areaServed": "IN",
              "availableLanguage": ["English", "Hindi"]
            },
            "founder": businessInfo.founder,
            "foundingDate": businessInfo.established,
            "industry": businessInfo.industry,
            "sameAs": [
              "https://linkedin.com/company/codrivatech",
              "https://github.com/codrivatech",
              "https://twitter.com/codrivatech",
              "https://facebook.com/codrivatech",
              "https://instagram.com/codrivatech"
            ]
          })
        }}
      />

      {/* Promotional Banner */}
      <div className="promo-banner">
        <div className="container">
          <div className="promo-content">
            <div className="promo-text">
              <FaPaperPlane className="promo-icon" />
              <div className="promo-text-content">
                <h3>Ready to Transform Your Digital Presence?</h3>
                <p>Get a free consultation and project estimate today!</p>
              </div>
            </div>
            <Link to="/contact" className="promo-button">
              Start Your Project
              <FiChevronUp className="rotate-90" />
            </Link>
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="trust-badges">
        <div className="container">
          <div className="badges-grid">
            <div className="badge-item">
              <FaShieldAlt className="badge-icon" />
              <span>Secure & Reliable</span>
            </div>
            <div className="badge-item">
              <FaCreditCard className="badge-icon" />
              <span>Flexible Payment Options</span>
            </div>
            <div className="badge-item">
              <FaGoogle className="badge-icon" />
              <span>Google Partner</span>
            </div>
            <div className="badge-item">
              <FiCheck className="badge-icon" />
              <span>100% Satisfaction</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Main Footer Content */}
        <div className="footer-main">
          {/* Company Info */}
          <div className="footer-brand" itemProp="brand">
            <div className="brand-wrapper">
              <div className="logo-container2">
                <img
                  src="/companylogo.png"
                  alt="Codriva Tech Logo - IT Development Company"
                  className="logo-image2"
                  itemProp="logo"
                  loading="lazy"
                />
                <div className="logo-glow"></div>
              </div>
              <div className="brand-content">
                <h3 className="brand-name" itemProp="name">{businessInfo.name}</h3>
                <p className="brand-tagline">
                  Building the Future, One Line of Code at a Time
                </p>
              </div>
            </div>

            <p className="company-description" itemProp="description">
              Your trusted partner in innovative web, mobile app, and software
              development solutions. We transform ideas into powerful digital products
              that drive business growth.
            </p>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h4 className="section-title">Quick Navigation</h4>
            <ul className="footer-links" itemScope itemType="https://schema.org/SiteNavigationElement">
              {quickLinks.map((link, index) => (
                <li key={index} className="link-item" itemProp="name">
                  <Link
                    to={link.path}
                    className="link"
                    itemProp="url"
                    aria-label={link.description}
                  >
                    <span className="link-dot"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div className="footer-section">
            <h4 className="section-title">Our Expertise</h4>
            <ul className="footer-services">
              {expertiseList.map((service, index) => (
                <li key={index} className="service-item">
                  <span>{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information */}
          <div className="footer-section contact-info" itemScope itemType="https://schema.org/ContactPoint">
            <h4 className="section-title">Contact Details</h4>
            <div className="contact-details">
              <div className="contact-item" itemProp="contactPoint">
                <div className="contact-icon">
                  <FaPhone />
                </div>
                <div className="contact-content">
                  <span className="contact-label">Call Us</span>
                  <a
                    href={`tel:${businessInfo.phone.replace(/\s/g, '')}`}
                    className="contact-value"
                    itemProp="telephone"
                  >
                    {businessInfo.phone}
                  </a>
                  <span className="contact-note">Available 9AM-6PM</span>
                </div>
              </div>

              <div className="contact-item" itemProp="contactPoint">
                <div className="contact-icon">
                  <FaEnvelope />
                </div>
                <div className="contact-content">
                  <span className="contact-label">Email Us</span>
                  <a
                    href={`mailto:${businessInfo.email}`}
                    className="contact-value"
                    itemProp="email"
                  >
                    {businessInfo.email}
                  </a>
                  <span className="contact-note">Response within 24 hours</span>
                </div>
              </div>

              <div className="contact-item" itemScope itemType="https://schema.org/PostalAddress">
                <div className="contact-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="contact-content">
                  <span className="contact-label">Visit Us</span>
                  <span className="contact-value" itemProp="streetAddress">
                    {businessInfo.address}
                  </span>
                  <span className="contact-note" itemProp="addressRegion">{businessInfo.hours}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="copyright-section">
          <p className="copyright" itemProp="copyrightYear">
            © {year} {businessInfo.name}. All rights reserved.
          </p>
        </div>

        {/* Scroll to Top Button */}
        <button
          className="scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll back to top"
        >
          <FiChevronUp />
          <span>Top</span>
        </button>
      </div>

      <style jsx>{`
        .footer {
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 40%,
            #1e293b 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
          padding-top: 60px;
          border-top: 1px solid rgba(34, 211, 238, 0.2);
        }

        /* Promotional Banner */
        .promo-banner {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          padding: 30px 0;
          margin-bottom: 60px;
          position: relative;
          overflow: hidden;
        }

        .promo-banner::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.1;
        }

        .promo-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .promo-text {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .promo-text-content {
          display: flex;
          flex-direction: column;
        }

        .promo-icon {
          font-size: 2.5rem;
          color: #020617;
          flex-shrink: 0;
        }

        .promo-text h3 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #020617;
          margin-bottom: 8px;
          line-height: 1.2;
        }

        .promo-text p {
          color: rgba(2, 6, 23, 0.8);
          font-size: 1rem;
          margin: 0;
        }

        .promo-button {
          padding: 15px 35px;
          background: #020617;
          color: #22d3ee;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s ease;
          box-shadow: 0 5px 20px rgba(2, 6, 23, 0.3);
          white-space: nowrap;
        }

        .promo-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(2, 6, 23, 0.4);
          background: #0f172a;
        }

        .rotate-90 {
          transform: rotate(90deg);
        }

        /* Trust Badges */
        .trust-badges {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          padding: 30px;
          margin-bottom: 60px;
          backdrop-filter: blur(10px);
        }

        .badges-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }

        .badge-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 15px;
          text-align: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 15px;
          transition: transform 0.3s ease;
        }

        .badge-item:hover {
          transform: translateY(-5px);
        }

        .badge-icon {
          font-size: 2.5rem;
          color: #22d3ee;
        }

        .badge-item span {
          font-size: 0.95rem;
          font-weight: 600;
          color: #f8fafc;
          line-height: 1.3;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
        }

        /* Main Footer Grid */
        .footer-main {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr 1.2fr;
          gap: 50px;
          margin-bottom: 80px;
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .footer.in-view .footer-main {
          opacity: 1;
          transform: translateY(0);
        }

        /* Brand Section */
        .footer-brand {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .brand-wrapper {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .logo-container2 {
          position: relative;
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }

        .logo-image2 {
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 20px;
          position: relative;
          z-index: 2;
        }

        .logo-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, 
            rgba(34, 211, 238, 0.2) 0%,
            transparent 70%);
          border-radius: 20px;
        }

        .brand-content {
          flex: 1;
        }

        .brand-name {
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 5px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
        }

        .brand-tagline {
          font-size: 0.95rem;
          color: #94a3b8;
          font-weight: 500;
          line-height: 1.4;
        }

        .company-description {
          font-size: 1rem;
          line-height: 1.7;
          color: #cbd5e1;
          margin: 0;
        }

        /* Section Titles */
        .section-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 25px;
          color: #22d3ee;
          position: relative;
          padding-bottom: 12px;
        }

        .section-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 50px;
          height: 3px;
          background: linear-gradient(90deg, #22d3ee, transparent);
          border-radius: 2px;
        }

        /* Links */
        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .link-item {
          position: relative;
        }

        .link {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #cbd5e1;
          text-decoration: none;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .link:hover {
          color: #22d3ee;
          transform: translateX(8px);
        }

        .link-dot {
          width: 6px;
          height: 6px;
          background: #22d3ee;
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .link:hover .link-dot {
          opacity: 1;
        }

        /* Services */
        .footer-services {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .service-item {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #cbd5e1;
          font-size: 0.9rem;
          transition: color 0.3s ease;
          line-height: 1.4;
        }

        .service-item:hover {
          color: #22d3ee;
        }

        .service-icon {
          color: #22d3ee;
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        /* Contact Info */
        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .contact-item {
          display: flex;
          gap: 15px;
          align-items: flex-start;
        }

        .contact-icon {
          width: 40px;
          height: 40px;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #22d3ee;
          font-size: 1.1rem;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .contact-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .contact-label {
          display: block;
          font-size: 0.85rem;
          color: #94a3b8;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .contact-value {
          display: block;
          font-size: 1rem;
          color: #f8fafc;
          text-decoration: none;
          margin-bottom: 4px;
          transition: color 0.3s ease;
          line-height: 1.4;
        }

        .contact-value:hover {
          color: #22d3ee;
        }

        .contact-note {
          display: block;
          font-size: 0.8rem;
          color: #64748b;
          line-height: 1.3;
          margin-top: 2px;
        }

        /* Copyright Section */
        .copyright-section {
          text-align: center;
          padding: 20px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .copyright {
          font-size: 1rem;
          color: #94a3b8;
          margin-bottom: 15px;
        }

        /* Scroll to Top */
        .scroll-top {
          position: fixed;
          bottom: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          border: none;
          border-radius: 12px;
          color: #020617;
          font-size: 1.5rem;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          transition: all 0.3s ease;
          z-index: 100;
          opacity: 0;
          transform: translateY(20px);
          box-shadow: 0 5px 20px rgba(34, 211, 238, 0.3);
        }

        .scroll-top:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(34, 211, 238, 0.4);
        }

        .scroll-top span {
          font-size: 0.7rem;
          font-weight: 600;
        }

        .scroll-top.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Animations */
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .footer-main {
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
          }

          .badges-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .promo-content {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .promo-text {
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .promo-icon {
            margin: 0 auto;
          }
        }

        @media (max-width: 768px) {
          .footer-main {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .container {
            padding: 0 20px;
          }

          .badges-grid {
            grid-template-columns: 1fr;
          }

          .promo-text h3 {
            font-size: 1.5rem;
          }

          .promo-button {
            padding: 12px 25px;
            font-size: 1rem;
          }

          .section-title {
            font-size: 1.2rem;
          }

          .brand-wrapper {
            flex-direction: column;
            text-align: center;
            gap: 15px;
          }

          .brand-content {
            text-align: center;
          }

          .logo-container2 {
            margin: 0 auto;
          }

          .contact-item {
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 10px;
          }

          .contact-icon {
            margin: 0 auto;
          }

          .scroll-top {
            bottom: 20px;
            right: 20px;
            width: 45px;
            height: 45px;
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .promo-banner {
            padding: 20px 0;
            margin-bottom: 40px;
          }

          .promo-text h3 {
            font-size: 1.3rem;
          }

          .trust-badges {
            padding: 20px;
            margin-bottom: 40px;
          }

          .footer {
            padding-top: 40px;
          }

          .footer-main {
            gap: 30px;
            margin-bottom: 40px;
          }

          .brand-name {
            font-size: 1.5rem;
          }

          .company-description {
            font-size: 0.9rem;
          }

          .link, .service-item, .contact-value {
            font-size: 0.9rem;
          }

          .copyright {
            font-size: 0.9rem;
          }
        }

        /* Tablet specific */
        @media (min-width: 769px) and (max-width: 1024px) {
          .footer-main {
            grid-template-columns: 1fr 1fr;
            gap: 40px;
          }

          .footer-brand {
            grid-column: 1 / -1;
            text-align: center;
          }

          .brand-wrapper {
            justify-content: center;
          }
        }

        .contact-value {
          word-break: break-word;
          overflow-wrap: break-word;
        }
      `}</style>
    </footer>
  );
};

export default Footer;